/* Safe to delete this entire file when removing FF `financial_categories_update` */
import {IntlShape} from 'react-intl';
import {type TooltipFormulaItem} from '@/components/TooltipFormulaRenderer';
import {createProjectPortfolioReportTooltip as createTooltip} from './ProjectPortfolioReportTooltip';
import {NullTooltip} from '@/forecast-app/shared/components/tooltips/FinancialCategoryTooltip';

type FormatMessage = IntlShape['formatMessage'];

/* Baseline */
const BaselineRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.revenue'}),
		details: [
			formatMessage({id: 'common.role'}),
			'x',

			formatMessage({id: 'project_budget.role_rate'}),
			'+',
			formatMessage({id: 'project_budget.expense_revenue'}),
		],
	},
];

const BaselineCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.cost'}),
		details: [
			formatMessage({id: 'project_budget.total_estimate'}),
			'x',
			formatMessage({id: 'common.role'}),
			'x',
			formatMessage({id: 'project_budget.adjusted_cost_rate'}),
			'+',
			formatMessage({id: 'project_budget.expense_cost'}),
		],
	},
];

const BaselineProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.profit'}),
		details: [
			formatMessage({id: 'project_budget.baseline_revenue'}),
			'-',
			formatMessage({id: 'project_budget.baseline_cost'}),
		],
	},
];

const BaselineMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.margin'}),
		details: [
			formatMessage({id: 'project_budget.baseline_profit'}),
			'/',
			formatMessage({id: 'project_budget.baseline_revenue'}),
			'X',
			'100',
		],
	},
];

/* Plan / Estimated */
const EstimatedBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.revenue'}),
		details: [formatMessage({id: 'project_budget.total_planned_revenue_all_projects'})],
	},
];

const EstimatedCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.cost'}),
		details: [formatMessage({id: 'project_budget.total_planned_cost_all_projects'})],
	},
];

const EstimatedProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.profit'}),
		details: [
			formatMessage({id: 'project_budget.planned_revenue'}),
			'-',
			formatMessage({id: 'project_budget.planned_cost'}),
		],
	},
];

const EstimatedMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.margin'}),
		details: [
			formatMessage({id: 'project_budget.planned_profit'}),
			'/',
			formatMessage({id: 'project_budget.planned_revenue'}),
			'X',
			'100',
		],
	},
];

/* Actual */
const ActualBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.revenue'}),
		details: [formatMessage({id: 'project_budget.total_actual_revenue_all_projects'})],
	},
];

const ActualCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.cost'}),
		details: [formatMessage({id: 'project_budget.total_actual_cost_all_projects'})],
	},
];

const ActualProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.profit'}),
		details: [
			formatMessage({id: 'project_budget.actual_revenue'}),
			'-',
			formatMessage({id: 'insights.component.list.column.actualCost'}),
		],
	},
];

const ActualMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.margin'}),
		details: [
			formatMessage({id: 'project_budget.actual_profit'}),
			'/',
			formatMessage({id: 'project_budget.actual_revenue'}),
			'X',
			'100',
		],
	},
];

/* Forecast to Complete / Remaining Work */
const RemainingWorkBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.revenue'}),
		details: [formatMessage({id: 'project_budget.total_remaining_revenue_all_projects'})],
	},
];

const RemainingWorkCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.cost'}),
		details: [formatMessage({id: 'project_budget.total_remaining_cost_all_projects'})],
	},
];

const RemainingWorkProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.profit'}),
		details: [
			formatMessage({id: 'project_budget.remaining_revenue'}),
			'-',
			formatMessage({id: 'project_budget.remaining_cost'}),
		],
	},
];

const RemainingWorkMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.margin'}),
		details: [
			formatMessage({id: 'project_budget.remaining_profit'}),
			'/',
			formatMessage({id: 'project_budget.remaining_revenue'}),
			'X',
			'100',
		],
	},
];

/* Forecasted / Projected Total */
const ProjectedTotalBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.revenue'}),
		details: [
			formatMessage({id: 'project_budget.actual_revenue'}),
			'+',
			formatMessage({id: 'project_budget.remaining_revenue_all_projects'}),
		],
	},
];

const ProjectedTotalCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.cost'}),
		details: [
			formatMessage({id: 'insights.component.list.column.actualCost'}),
			'+',
			formatMessage({id: 'project_budget.remaining_cost_all_projects'}),
		],
	},
];

const ProjectedTotalProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.profit'}),
		details: [
			formatMessage({id: 'project_budget.forecast_revenue'}),
			'-',
			formatMessage({id: 'project_budget.forecast_cost'}),
		],
	},
];

const ProjectedTotalMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.margin'}),
		details: [
			formatMessage({id: 'project_budget.forecast_profit'}),
			'/',
			formatMessage({id: 'project_budget.forecast_revenue'}),
			'X',
			'100',
		],
	},
];

/* Project Overview */
const ProjectOverviewWinProbabilityTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_portfolio_report.win_probability'}),
	},
];

/* Project Hours */
const ProjectHoursBaselineHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.all_estimated_hours'}),
	},
];

const ProjectHoursEstimatedHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		details: [formatMessage({id: 'project_portfolio_report.approved_scope_tooltip'})],
	},
];

const ProjectHoursActualHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.all_time_entries'}),
	},
];

const ProjectHoursRemainingWorkHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.all_remaining_hours'}),
	},
];

const ProjectHoursProjectedTotalHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		details: [
			formatMessage({id: 'project_budget.all_time_entries'}),
			'+',
			formatMessage({id: 'project_budget.all_remaining_hours'}),
		],
	},
];

export const ProjectPortfolioReportHeadersTooltips = {
	BaselineRevenueTooltip: createTooltip(BaselineRevenueTooltipItems),
	BaselineBillableValueOfServiceTooltip: NullTooltip,
	BaselineCostTooltip: createTooltip(BaselineCostTooltipItems),
	BaselineProfitTooltip: createTooltip(BaselineProfitTooltipItems),
	BaselineMarginTooltip: createTooltip(BaselineMarginTooltipItems),

	EstimatedRevenueTooltip: NullTooltip,
	EstimatedBillableValueOfServiceTooltip: createTooltip(EstimatedBillableValueOfServiceTooltipItems),
	EstimatedCostTooltip: createTooltip(EstimatedCostTooltipItems),
	EstimatedProfitTooltip: createTooltip(EstimatedProfitTooltipItems),
	EstimatedMarginTooltip: createTooltip(EstimatedMarginTooltipItems),

	ActualRevenueTooltip: NullTooltip,
	ActualBillableValueOfServiceTooltip: createTooltip(ActualBillableValueOfServiceTooltipItems),
	ActualCostTooltip: createTooltip(ActualCostTooltipItems),
	ActualProfitTooltip: createTooltip(ActualProfitTooltipItems),
	ActualMarginTooltip: createTooltip(ActualMarginTooltipItems),

	RemainingWorkRevenueTooltip: NullTooltip,
	RemainingWorkBillableValueOfServiceTooltip: createTooltip(RemainingWorkBillableValueOfServiceTooltipItems),
	RemainingWorkCostTooltip: createTooltip(RemainingWorkCostTooltipItems),
	RemainingWorkProfitTooltip: createTooltip(RemainingWorkProfitTooltipItems),
	RemainingWorkMarginTooltip: createTooltip(RemainingWorkMarginTooltipItems),

	ProjectedTotalRevenueTooltip: NullTooltip,
	ProjectedTotalBillableValueOfServiceTooltip: createTooltip(ProjectedTotalBillableValueOfServiceTooltipItems),
	ProjectedTotalCostTooltip: createTooltip(ProjectedTotalCostTooltipItems),
	ProjectedTotalProfitTooltip: createTooltip(ProjectedTotalProfitTooltipItems),
	ProjectedTotalMarginTooltip: createTooltip(ProjectedTotalMarginTooltipItems),

	InvoicedInvoicedTooltip: NullTooltip,
	InvoicedInvoicePaidTooltip: NullTooltip,

	RevenueRecognitionFromLockedMonthsTooltip: NullTooltip,
	RevenueRecognitionFromOpenMonthsTooltip: NullTooltip,
	FixedPriceTooltip: NullTooltip,

	EstimatedVsActualRevenueTooltip: NullTooltip,
	EstimatedVsActualCostTooltip: NullTooltip,
	EstimatedVsActualProfitTooltip: NullTooltip,
	EstimatedVsActualBillableValueOfServiceTooltip: NullTooltip,

	EstimatedVsProjectedTotalRevenueTooltip: NullTooltip,
	EstimatedVsProjectedTotalCostTooltip: NullTooltip,
	EstimatedVsProjectedTotalProfitTooltip: NullTooltip,
	EstimatedVsProjectedTotalBillableValueOfServiceTooltip: NullTooltip,

	OverUnderServicingProjectedTotalRevenueTooltip: NullTooltip,
	OverUnderServicingProjectedTotalBillableValueOfServiceTooltip: NullTooltip,
	OverUnderServicingProjectedTotalNonBillableValueOfServiceTooltip: NullTooltip,
	OverUnderServicingProjectedTotalValueOfServiceTooltip: NullTooltip,
	OverUnderServicingProjectedRemainingBudgetTooltip: NullTooltip,
	OverUnderServicingProjectedOverUnderServicingTooltip: NullTooltip,

	AccruedDeferredInvoicedTooltip: NullTooltip,
	AccruedDeferredRevenueRecognitionFromLockedMonthsTooltip: NullTooltip,
	AccruedDeferredAccruedDeferredTooltip: NullTooltip,

	RetainerPeriodTargetTimeTooltip: NullTooltip,
	RetainerPeriodTargetPriceTooltip: NullTooltip,

	ProjectOverviewPriorityLevelTooltip: NullTooltip,
	ProjectOverviewWinProbabilityTooltip: createTooltip(ProjectOverviewWinProbabilityTooltipItems),
	ProjectOverviewProjectStartDateTooltip: NullTooltip,
	ProjectOverviewProjectEndDateTooltip: NullTooltip,
	ProjectOverviewClientTooltip: NullTooltip,
	ProjectOverviewStatusTooltip: NullTooltip,
	ProjectOverviewStatusDescriptionTooltip: NullTooltip,
	ProjectOverviewProjectOwnerTooltip: NullTooltip,
	ProjectOverviewStageTooltip: NullTooltip,
	ProjectOverviewBudgetTypeTooltip: NullTooltip,
	ProjectOverviewRateCardTooltip: NullTooltip,
	ProjectOverviewProjectProgressTooltip: NullTooltip,

	ProjectHoursBaselineHoursTooltip: createTooltip(ProjectHoursBaselineHoursTooltipItems),
	ProjectHoursEstimatedHoursTooltip: createTooltip(ProjectHoursEstimatedHoursTooltipItems),
	ProjectHoursActualHoursTooltip: createTooltip(ProjectHoursActualHoursTooltipItems),
	ProjectHoursRemainingWorkHoursTooltip: createTooltip(ProjectHoursRemainingWorkHoursTooltipItems),
	ProjectHoursProjectedTotalHoursTooltip: createTooltip(ProjectHoursProjectedTotalHoursTooltipItems),
};
